import { useEffect, useRef, useState } from "react";
import client, { apiError, API } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  ShieldCheck, DownloadSimple, UploadSimple, Buildings, Plus,
  DatabaseIcon, SignOut, Users, Storefront, Note, Receipt, Trash, Warning, ArrowUUpLeft,
} from "@phosphor-icons/react";

const SECTIONS = [
  { key: "retailers",  label: "Retailers",  icon: Storefront,   endpoint: "retailers" },
  { key: "users",      label: "Users",      icon: Users,        endpoint: "users" },
  { key: "box-notes",  label: "Box Notes",  icon: Note,         endpoint: "box-notes" },
  { key: "grsr",       label: "GRSR",       icon: Receipt,      endpoint: "grsr" },
];

function download(path, filename) {
  const token = localStorage.getItem("token");
  return fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    });
}

export default function AdminConsole() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("retailers");
  const [companies, setCompanies] = useState([]);
  const [companyForm, setCompanyForm] = useState({ name: "", address: "", phone: "", gst: "", pan: "" });
  const [busy, setBusy] = useState(false);
  const [revertOrderNo, setRevertOrderNo] = useState("");
  const fileRef = useRef({});

  const loadCompanies = async () => {
    try { const { data } = await client.get("/admin/companies"); setCompanies(data); } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { loadCompanies(); }, []);

  const addCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.name.trim()) return toast.error("Company name required");
    setBusy(true);
    try {
      await client.post("/admin/companies", companyForm);
      toast.success("Company added");
      setCompanyForm({ name: "", address: "", phone: "", gst: "", pan: "" });
      loadCompanies();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const exportCsv = async (key, endpoint) => {
    try { await download(`/admin/export/${endpoint}`, `${key}.csv`); toast.success("Downloaded"); }
    catch (e) { toast.error(e.message); }
  };

  const importCsv = async (key, endpoint, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const { data } = await client.post(`/admin/import/${endpoint}`, { csv: text });
      const skipped = (data.skipped || []).length;
      const msg = [
        data.inserted != null ? `${data.inserted} inserted` : null,
        data.updated != null ? `${data.updated} updated` : null,
        skipped ? `${skipped} skipped` : null,
      ].filter(Boolean).join(", ");
      toast.success(`Import complete: ${msg || "no rows"}`);
      if (skipped && data.skipped) console.warn("Skipped rows:", data.skipped);
    } catch (e) { toast.error(apiError(e)); } finally {
      setBusy(false);
      if (fileRef.current[key]) fileRef.current[key].value = "";
    }
  };

  const downloadBackup = async () => {
    setBusy(true);
    try { await download("/admin/backup", `backup-${new Date().toISOString().slice(0,10)}.zip`); toast.success("Backup downloaded"); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const revertOrderToOrdered = async () => {
    const on = revertOrderNo.trim();
    if (!on) return toast.error("Enter an Order No first");
    if (!window.confirm(
      `Revert order "${on}" to the 'Ordered' stage?\n\nThis will clear ALL picking, scanning, packing, and dispatch entries for that order. Any dispatch label, bill, and LR data will be lost.`
    )) return;
    setBusy(true);
    try {
      const { data } = await client.post("/admin/revert-order-to-ordered", { order_no: on });
      toast.success(data.message || `Reverted "${on}" to Ordered`);
      setRevertOrderNo("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const deleteAllGrsr = async () => {
    const phrase = window.prompt(
      "⚠️  This will PERMANENTLY delete ALL GRSR records.\n\nType DELETE ALL exactly to confirm."
    );
    if (phrase == null) return; // cancelled
    if (phrase.trim() !== "DELETE ALL") {
      return toast.error("Confirmation phrase did not match. Nothing was deleted.");
    }
    setBusy(true);
    try {
      const { data } = await client.request({
        method: "DELETE",
        url: "/admin/grsr/all",
        data: { confirm: "DELETE ALL" },
      });
      toast.success(`Deleted ${data.deleted} GRSR records`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const doLogout = () => { logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-950 text-slate-100 border-b-4 border-orange-500">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-sm bg-orange-500/20 border border-orange-500 text-orange-300 grid place-items-center">
              <ShieldCheck size={20} weight="duotone" />
            </div>
            <div>
              <div className="label-cap text-orange-300">Restricted Access</div>
              <h1 className="font-display text-xl font-bold">Admin Console</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm">{user.name}</div>
              <div className="text-xs text-slate-400 font-mono">{user.mobile}</div>
            </div>
            <button onClick={doLogout} data-testid="admin-logout-btn" className="p-2 rounded-sm bg-slate-800 hover:bg-slate-700">
              <SignOut size={18} weight="bold"/>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Companies */}
        <section className="bg-white border border-slate-200 rounded-sm">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
            <Buildings size={22} weight="duotone" className="text-orange-600" />
            <div>
              <div className="label-cap">Master Data</div>
              <h2 className="font-display text-lg font-bold">Companies</h2>
            </div>
          </div>
          <div className="p-6 grid md:grid-cols-2 gap-8">
            <form onSubmit={addCompany} className="space-y-3" data-testid="add-company-form">
              <div><label className="label-cap">Name *</label><Input data-testid="ac-company-name" value={companyForm.name} onChange={(e)=>setCompanyForm({...companyForm, name: e.target.value})}/></div>
              <div><label className="label-cap">Address</label><Input value={companyForm.address} onChange={(e)=>setCompanyForm({...companyForm, address: e.target.value})}/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label-cap">Phone</label><Input value={companyForm.phone} onChange={(e)=>setCompanyForm({...companyForm, phone: e.target.value})}/></div>
                <div><label className="label-cap">GST</label><Input value={companyForm.gst} onChange={(e)=>setCompanyForm({...companyForm, gst: e.target.value})}/></div>
              </div>
              <div><label className="label-cap">PAN</label><Input value={companyForm.pan} onChange={(e)=>setCompanyForm({...companyForm, pan: e.target.value})}/></div>
              <Button type="submit" disabled={busy} data-testid="ac-company-add-btn" className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm">
                <Plus size={16} className="mr-1"/> Add Company
              </Button>
            </form>
            <div>
              <div className="label-cap mb-2">Existing ({companies.length})</div>
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 label-cap text-left">
                    <th className="px-3 py-2">Name</th><th className="px-3 py-2">Phone</th><th className="px-3 py-2">GST</th>
                  </tr></thead>
                  <tbody>
                    {companies.map(c=>(
                      <tr key={c.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-semibold">{c.name}</td>
                        <td className="px-3 py-2 font-mono">{c.phone || "—"}</td>
                        <td className="px-3 py-2 font-mono">{c.gst || "—"}</td>
                      </tr>
                    ))}
                    {companies.length===0 && <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-500">No companies</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* CSV Import/Export */}
        <section className="bg-white border border-slate-200 rounded-sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="label-cap">Bulk Data</div>
            <h2 className="font-display text-lg font-bold">Import / Export CSV</h2>
          </div>
          <div className="p-6">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-slate-100 rounded-sm">
                {SECTIONS.map(s=>{const I=s.icon;return (
                  <TabsTrigger key={s.key} value={s.key} data-testid={`ac-tab-${s.key}`} className="data-[state=active]:bg-orange-600 data-[state=active]:text-white rounded-sm">
                    <I size={14} className="mr-1"/>{s.label}
                  </TabsTrigger>
                );})}
              </TabsList>

              {SECTIONS.map(s=>(
                <TabsContent key={s.key} value={s.key} className="mt-6 grid md:grid-cols-2 gap-6">
                  <div className="border border-slate-200 rounded-sm p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <DownloadSimple size={20} weight="duotone" className="text-emerald-600"/>
                      <h3 className="font-display font-bold">Export {s.label}</h3>
                    </div>
                    <p className="text-sm text-slate-600 mb-4">Download all {s.label.toLowerCase()} as a CSV file. Passwords are never exported.</p>
                    <Button data-testid={`ac-export-${s.key}`} onClick={()=>exportCsv(s.key, s.endpoint)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
                      <DownloadSimple size={16} className="mr-1"/> Download CSV
                    </Button>
                  </div>

                  <div className="border border-slate-200 rounded-sm p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <UploadSimple size={20} weight="duotone" className="text-blue-600"/>
                      <h3 className="font-display font-bold">Import {s.label}</h3>
                    </div>
                    <p className="text-sm text-slate-600 mb-3">Upload a CSV. New rows are inserted; existing rows (by unique key) are updated.</p>
                    <input ref={(el)=>fileRef.current[s.key]=el} type="file" accept=".csv" onChange={(e)=>importCsv(s.key, s.endpoint, e.target.files?.[0])}
                           data-testid={`ac-import-${s.key}`}
                           className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer"/>
                    <div className="mt-3 text-xs text-slate-500">
                      {s.key === "retailers" && <span>Columns: <code className="font-mono">retailer_code, name, address, area, city, state, pincode, phone, email, gst, salesman</code></span>}
                      {s.key === "users"     && <span>Columns: <code className="font-mono">mobile, name, role, password</code> (password required for new users)</span>}
                      {s.key === "box-notes" && <span>Columns: <code className="font-mono">box_details, retailer_code, company_name, from_location, reason, retailer_doc_no, retailer_doc_date, retailer_doc_quantity, total_box_received, mode_of_transport, lr_no, retailer_received_by</code></span>}
                      {s.key === "grsr"      && <span>Columns: <code className="font-mono">grsr_no, grsr_date, retailer_code, company_name, salesman_name, actual_quantity_received, mode_of_transport, builty_no, total_shipper, material_booked_by, retailer_doc_no, retailer_doc_date, document_quantity, quickbill_no, quickbill_cn_date, shopper_cn_no, shopper_cn_date, saleable_with_box, saleable_without_box, non_moving, defective, saleable_without_box_pb_tag_sticker, damaged, remark</code></span>}
                    </div>
                  </div>

                  {s.key === "grsr" && (
                    <div className="md:col-span-2 border border-rose-300 bg-rose-50 rounded-sm p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Warning size={20} weight="fill" className="text-rose-600"/>
                        <h3 className="font-display font-bold text-rose-900">Danger Zone — Delete All GRSR</h3>
                      </div>
                      <p className="text-sm text-rose-800 mb-4">
                        Permanently removes every GRSR record from the database. This action is <b>irreversible</b>.
                        You'll be asked to type <code className="font-mono bg-white px-1 py-0.5 rounded-sm border border-rose-200">DELETE ALL</code> to confirm.
                        Consider downloading a backup first.
                      </p>
                      <Button
                        data-testid="ac-delete-all-grsr"
                        onClick={deleteAllGrsr}
                        disabled={busy}
                        className="bg-rose-600 hover:bg-rose-700 text-white rounded-sm"
                      >
                        <Trash size={16} className="mr-1" weight="bold"/> Delete ALL GRSR Records
                      </Button>
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </section>

        {/* Order recovery — Revert to Ordered */}
        <section className="bg-white border border-amber-200 rounded-sm overflow-hidden mb-6" data-testid="ac-revert-section">
          <div className="px-6 py-4 border-b border-amber-200 flex items-center gap-3 bg-amber-50/60">
            <ArrowUUpLeft size={22} weight="duotone" className="text-amber-700" />
            <div>
              <div className="label-cap text-amber-800">Order recovery</div>
              <h2 className="font-display text-lg font-bold text-amber-950">Revert to Ordered State</h2>
            </div>
          </div>
          <div className="p-6">
            <p className="text-sm text-amber-900/80 mb-4 max-w-2xl">
              Wipes <b>picking, scanning, packing and dispatch</b> data for the specified order and resets its stage to <code className="font-mono text-xs bg-amber-100 px-1 py-0.5 rounded-sm">ordered</code>. Any dispatch label, bill number and LR details will be lost — use only for genuine mistakes.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[240px] max-w-md">
                <label className="label-cap">Order No</label>
                <Input
                  data-testid="ac-revert-order-input"
                  value={revertOrderNo}
                  onChange={(e)=>setRevertOrderNo(e.target.value)}
                  placeholder="Exact Order No (case-sensitive)"
                  onKeyDown={(e)=>{ if (e.key === "Enter" && revertOrderNo.trim()) revertOrderToOrdered(); }}
                />
              </div>
              <Button
                data-testid="ac-revert-order-btn"
                onClick={revertOrderToOrdered}
                disabled={busy || !revertOrderNo.trim()}
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm h-11"
              >
                <ArrowUUpLeft size={16} className="mr-1" weight="bold"/>
                {busy ? "Working…" : "Revert to Ordered State"}
              </Button>
            </div>
          </div>
        </section>

        {/* Backup */}
        <section className="bg-orange-50 border border-orange-200 rounded-sm">
          <div className="px-6 py-4 border-b border-orange-200 flex items-center gap-3">
            <DatabaseIcon size={22} weight="duotone" className="text-orange-700" />
            <div>
              <div className="label-cap text-orange-800">Full System</div>
              <h2 className="font-display text-lg font-bold text-orange-950">Database Backup</h2>
            </div>
          </div>
          <div className="p-6">
            <p className="text-sm text-orange-900/80 mb-4 max-w-2xl">
              Download a complete backup of every collection in the database as a ZIP archive.
              Each collection is exported as a separate JSON file (MongoDB is a document store — JSON is the
              standard, restorable backup format; equivalent of a SQL dump). Includes a <code className="font-mono text-xs bg-white px-1">manifest.json</code> and a <code className="font-mono text-xs bg-white px-1">README.txt</code> with restore instructions using <code className="font-mono text-xs bg-white px-1">mongoimport</code>.
            </p>
            <Button data-testid="ac-backup-btn" onClick={downloadBackup} disabled={busy} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm h-11">
              <DownloadSimple size={16} className="mr-1"/> {busy ? "Preparing…" : "Download Full Backup (ZIP)"}
            </Button>
          </div>
        </section>
      </main>
      <Toaster richColors position="top-right" />
    </div>
  );
}
